/* External */
import {
  memo,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { Map } from 'immutable';

/* Utils */
import { useIsCursorVisible } from '../CursorStateProvider';
import { useKeyBoardState } from '../KeyBoardStateProvider';
import { restoreFocusStrategy } from './utils';
import { RestorableFocusContext, ToggleSpatialNavigationContext, FocusRestoredContext } from './contexts';
import type { FocusRestorableComponent } from './withFocusRestorable.hoc';
import type { RestorableFocusContextType, ToggleSpatialNavigationContextType, FocusRestoredContextType } from './contexts';

interface RestoreFocusProviderProps {
  setFocus: (focusKey?: string) => void,
  pauseSpatialNavigation: () => void,
  resumeSpatialNavigation: () => void,
  children?: ReactNode,
}

type FocusRestorableCollection = Map<string, FocusRestorableComponent>;

function RestoreFocusProvider({
  setFocus,
  pauseSpatialNavigation,
  resumeSpatialNavigation,
  children,
}: RestoreFocusProviderProps) {
  const focusableComponents = useRef<Map<string, FocusRestorableComponent>>();
  const lastHoveredRealFocusKey = useRef<string>('');
  const isCursorVisible = useIsCursorVisible();
  const isCursorVisibleRef = useRef(isCursorVisible);
  const lastIsCursorVisible = useRef(false);
  const spatialNavigationPaused = useRef(false);
  const [isFocusRestored, setIsFocusRestored] = useState(true);
  const [isCursorVisibleState, setIsCursorVisibleState] = useState(isCursorVisible);
  const [cursorVisibilityChanged, setCursorVisibilityChanged] = useState(false);
  const [shouldResumeSpatialNavigation, setShouldResumeSpatialNavigation] = useState(false);
  const isKeyboardVisible = useKeyBoardState();
  const shouldRestoreFocusAtKeyboardHidden = useRef<boolean>(false);

  const getFocusableComponents = useCallback((): FocusRestorableCollection => {
    if (!focusableComponents.current) {
      focusableComponents.current = Map();
    }

    return focusableComponents.current;
  }, []);

  isCursorVisibleRef.current = isCursorVisible;

  const restoreFocus = useCallback(() => {
    shouldRestoreFocusAtKeyboardHidden.current = false;

    const focusKeyToRestoreFocusTo = restoreFocusStrategy(
      getFocusableComponents().toSeq(),
      lastHoveredRealFocusKey.current,
    );
    if (focusKeyToRestoreFocusTo) {
      const component = getFocusableComponents().get(focusKeyToRestoreFocusTo);
      if (component) component.onFocusRestored();

      setFocus(focusKeyToRestoreFocusTo);
    }
    setIsFocusRestored(true);
  }, [getFocusableComponents, setFocus]);

  useEffect(() => {
    setCursorVisibilityChanged(true);
  }, [isCursorVisible]);

  useEffect(() => {
    setCursorVisibilityChanged(false);
  }, [isFocusRestored]);

  useEffect(() => {
    if (lastIsCursorVisible.current !== isCursorVisible) {
      lastIsCursorVisible.current = isCursorVisible;

      if (isCursorVisible) {
        setIsFocusRestored(false);
        pauseSpatialNavigation();
      } else {
        if (isKeyboardVisible) {
          shouldRestoreFocusAtKeyboardHidden.current = true;
        } else {
          restoreFocus();
        }
        if (!spatialNavigationPaused.current) {
          setShouldResumeSpatialNavigation(true);
        }
      }

      setIsCursorVisibleState(isCursorVisible);
    }
  }, [
    isCursorVisible,
    isKeyboardVisible,
    pauseSpatialNavigation,
    restoreFocus,
    setFocus,
  ]);

  useEffect(() => {
    if (shouldResumeSpatialNavigation) {
      resumeSpatialNavigation();
      setShouldResumeSpatialNavigation(false);
    }
  }, [resumeSpatialNavigation, shouldResumeSpatialNavigation]);

  useEffect(() => {
    if (!isKeyboardVisible && shouldRestoreFocusAtKeyboardHidden.current) {
      restoreFocus();
    }
  }, [isKeyboardVisible, restoreFocus]);

  const setLastHoveredRealFocusKey = useCallback((newLastHoveredRealFocusKey: string) => {
    const component = getFocusableComponents().get(newLastHoveredRealFocusKey);
    if (component && !component.ignoreHoverToRestoreFocus()) {
      lastHoveredRealFocusKey.current = newLastHoveredRealFocusKey;
    }
  }, [getFocusableComponents]);

  const addFocusableComponent = useCallback(
    (realFocusKey: string, componentReference: FocusRestorableComponent) => {
      focusableComponents.current = getFocusableComponents().set(
        realFocusKey,
        componentReference,
      );
    },
    [getFocusableComponents],
  );

  const removeFocusableComponent = useCallback((realFocusKey: string) => {
    focusableComponents.current = getFocusableComponents().remove(realFocusKey);
  }, [getFocusableComponents]);

  const restorableFocusValue = useMemo((): RestorableFocusContextType => ({
    addFocusableComponent,
    removeFocusableComponent,
    setLastHoveredRealFocusKey,
  }), [
    addFocusableComponent,
    removeFocusableComponent,
    setLastHoveredRealFocusKey,
  ]);

  const pauseSpatialNavigationProxy = useCallback(() => {
    if (!spatialNavigationPaused.current) {
      spatialNavigationPaused.current = true;
      pauseSpatialNavigation();
    }
  }, [pauseSpatialNavigation]);

  const resumeSpatialNavigationProxy = useCallback(() => {
    if (spatialNavigationPaused.current) {
      spatialNavigationPaused.current = false;
      if (!isCursorVisibleRef.current) {
        setShouldResumeSpatialNavigation(true);
      }
    }
  }, []);

  const toggleSpatialNavigationValue = useMemo((): ToggleSpatialNavigationContextType => ({
    pauseSpatialNavigation: pauseSpatialNavigationProxy,
    resumeSpatialNavigation: resumeSpatialNavigationProxy,
  }), [pauseSpatialNavigationProxy, resumeSpatialNavigationProxy]);

  const focusRestoredValue = useMemo((): FocusRestoredContextType => ({
    isFocusRestored,
    isCursorVisible: isCursorVisibleState,
    cursorVisibilityChanged,
  }), [cursorVisibilityChanged, isCursorVisibleState, isFocusRestored]);

  return (
    <RestorableFocusContext.Provider value={restorableFocusValue}>
      <ToggleSpatialNavigationContext.Provider value={toggleSpatialNavigationValue}>
        <FocusRestoredContext.Provider value={focusRestoredValue}>
          {children}
        </FocusRestoredContext.Provider>
      </ToggleSpatialNavigationContext.Provider>
    </RestorableFocusContext.Provider>
  );
}

export default memo(withFocusable()(memo(RestoreFocusProvider)));
